import React, { useState, useMemo } from "react";
import {
  CheckCircle2,
  XCircle,
  User,
  AlertCircle,
  Eye,
  Pencil,
  Trash2,
  Fish,
  ChevronUp,
  ChevronDown,
  Download,
  Briefcase,
  Mail,
  Phone,
  Activity,
  Settings,
  Search
} from "lucide-react";
import { motion } from "framer-motion";
import PaginationFooter from "../../../components/ui/PaginationFooter";
import Tooltip from "../../../components/ui/Tooltip";
import MiniLoader from "../../../components/MiniLoader";
import { useModal } from "../../../context/ModalContext";

const getStatusInfo = (status) => {
  switch (status) {
    case "CONVERTED":
      return { color: "bg-success-50 text-success-700 border-success-200", icon: <CheckCircle2 className="w-4 h-4 text-success-500" /> };
    case "DISQUALIFIED":
      return { color: "bg-danger-50 text-danger-700 border-danger-200", icon: <XCircle className="w-4 h-4 text-danger-500" /> };
    case "QUALIFIED":
      return { color: "bg-primary-50 text-primary-700 border-primary-200", icon: <User className="w-4 h-4 text-primary-500" /> };
    case "NEW":
    case "PENDING":
      return { color: "bg-warning-50 text-warning-700 border-warning-200", icon: <AlertCircle className="w-4 h-4 text-warning-500" /> };
    default:
      return { color: "bg-surface-muted text-subtle border-workspace", icon: <User className="w-4 h-4 text-muted" /> };
  }
};

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center h-64 w-full space-y-6">
      <motion.div
        animate={{ y: [0, -10, 0], rotate: [0, -15, 10, -5, 0] }}
        transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
        className="text-primary-500"
      >
        <Fish className="w-16 h-16" />
      </motion.div>
      <p className="text-muted text-sm font-medium">Aucun prospect trouvé.</p>
    </div>
  );
}

export default function ProspectsTable({
  prospects = [],
  totalProspects = 0,
  loading,
  refreshing = false,
  error,
  onRetry,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  selectProspect,
  onEdit,
  selectedRows = new Set(),
  setSelectedRows,
  onExport,
  onViewDetails,
  onArchive,
}) {
  const [sortColumn, setSortColumn] = useState('lastName');
  const [sortDirection, setSortDirection] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const { confirm } = useModal();

  // Appliquer le tri et le filtre
  const sortedProspects = useMemo(() => {
    let filtered = [...prospects];
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        Object.values(p).some(val => val && val.toString().toLowerCase().includes(lowerSearch))
      );
    }
    
    filtered.sort((a, b) => {
      let valA = a[sortColumn] ? a[sortColumn].toString().toLowerCase() : "";
      let valB = b[sortColumn] ? b[sortColumn].toString().toLowerCase() : "";
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return filtered;
  }, [prospects, sortColumn, sortDirection, searchTerm]);

  const handleSort = (column) => {
    if (loading || refreshing) return;
    setSelectedRows(new Set());
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) return <ChevronDown className="w-4 h-4 text-workspace opacity-0 group-hover:opacity-100 transition-opacity" />;
    return sortDirection === 'asc'
      ? <ChevronUp className="w-4 h-4 text-primary" />
      : <ChevronDown className="w-4 h-4 text-primary" />;
  };

  const handleArchive = async (id, e) => {
    e.stopPropagation();
    
    const isConfirmed = await confirm({
      title: "Archiver le prospect",
      message: "Êtes-vous sûr de vouloir archiver ce prospect ? Il ne sera plus visible dans la liste active.",
      confirmText: "Archiver",
      cancelText: "Annuler",
      type: "danger"
    });

    if (!isConfirmed) return;

    if (onArchive) {
      onArchive(id);
    }
  };

  const toggleAll = () => {
    if (sortedProspects.length > 0 && sortedProspects.every(p => selectedRows.has(p._id || p.id))) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(sortedProspects.map(p => p._id || p.id)));
    }
  };

  const toggleRow = (id, e) => {
    e.stopPropagation();
    const newSet = new Set(selectedRows);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedRows(newSet);
  };

  const currentPage = page ?? 0;
  const selectedVisibleCount = sortedProspects.filter(p => selectedRows.has(p._id || p.id)).length;
  const isAllSelected = sortedProspects.length > 0 && selectedVisibleCount === sortedProspects.length;
  const isIndeterminate = selectedVisibleCount > 0 && !isAllSelected;
  const busy = Boolean(loading || refreshing);
  const summaryText = searchTerm
    ? `${sortedProspects.length} résultat(s) sur cette page ; ${totalProspects} résultats au total`
    : undefined;

  return (
    <div className="workspace-card flex flex-col h-full bg-surface shadow-hp-sm border-workspace rounded-xl overflow-hidden relative" aria-busy={busy}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-workspace px-6 py-3">
        <div className="workspace-search-control overflow-hidden w-full sm:w-[300px] bg-surface h-9">
          <Search className="w-4 h-4 text-subtle flex-shrink-0" aria-hidden="true" />
          <input
            type="search"
            aria-label="Rechercher sur cette page de prospects"
            className="workspace-search-input ml-2 text-sm h-full"
            placeholder="Rechercher sur cette page..."
            value={searchTerm}
            disabled={busy}
            onChange={event => {
              setSearchTerm(event.target.value);
              setSelectedRows(new Set());
            }}
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs text-subtle">Sélection limitée à cette page</span>
          {selectedVisibleCount > 0 && (
            <button
              type="button"
              onClick={onExport}
              disabled={busy || Boolean(error)}
              className="btn btn-secondary h-8 px-3 rounded-lg flex items-center gap-2 font-medium text-sm whitespace-nowrap"
            >
              <Download className="w-3.5 h-3.5" aria-hidden="true" />
              Exporter ({selectedVisibleCount})
            </button>
          )}
        </div>
      </div>
      {error && (
        <div role="alert" className="px-6 py-3 border-b border-workspace text-sm text-danger-600">
          Impossible de charger les prospects.
          {prospects.length > 0 && " Les dernières données disponibles sont affichées."}
          <button type="button" className="btn btn-secondary ml-3" disabled={busy} onClick={() => onRetry?.()}>
            Réessayer
          </button>
        </div>
      )}
      {refreshing && !loading && <p role="status" className="px-6 py-2 text-sm text-subtle">Chargement de la page demandée…</p>}
      {/* Table Container */}
      <div className="flex-1 min-h-0 overflow-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-surface sticky top-0 z-10 border-b border-workspace">
            <tr>
              <th scope="col" className="px-6 py-3 text-left w-[40px]">
                <input
                  type="checkbox"
                  className="w-4 h-4 text-primary bg-surface border-tertiary rounded focus:ring-primary-500 cursor-pointer"
                  aria-label="Sélectionner tous les prospects visibles sur cette page"
                  disabled={busy || sortedProspects.length === 0}
                  checked={isAllSelected}
                  ref={input => {
                    if (input) input.indeterminate = isIndeterminate;
                  }}
                  onChange={toggleAll}
                />
              </th>
              {[
                { label: "Type", key: "type", icon: <Briefcase className="w-3.5 h-3.5" /> },
                { label: "Nom / Entreprise", key: "lastName", icon: <User className="w-3.5 h-3.5" /> },
                { label: "Prénom", key: "firstName", icon: <User className="w-3.5 h-3.5" /> },
                { label: "Email", key: "email", icon: <Mail className="w-3.5 h-3.5" /> },
                { label: "Téléphone", key: "phone", icon: <Phone className="w-3.5 h-3.5" /> },
                { label: "Statut", key: "status", icon: <Activity className="w-3.5 h-3.5" /> }
              ].map((col) => (
                <th
                  key={col.label}
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-semibold text-subtle uppercase tracking-wider cursor-pointer group hover:text-primary transition-colors select-none"
                  onClick={() => handleSort(col.key)}
                >
                  <div className="flex items-center gap-1.5">
                    {col.icon}
                    {col.label}
                    <SortIcon column={col.key} />
                  </div>
                </th>
              ))}
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-subtle uppercase tracking-wider">
                <div className="flex items-center justify-center gap-1.5">
                  <Settings className="w-3.5 h-3.5" /> Actions
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-surface divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan="8" className="px-6 py-10">
                  <div className="flex justify-center">
                    <MiniLoader />
                  </div>
                </td>
              </tr>
            ) : error && prospects.length === 0 ? null : sortedProspects.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-10">
                  <EmptyState />
                </td>
              </tr>
            ) : (
              sortedProspects.map((prospect) => {
                const isSelected = selectedRows.has(prospect._id || prospect.id);
                return (
                  <tr
                    key={prospect._id || prospect.id}
                    className={`${isSelected ? 'bg-primary-50' : 'hover:bg-accent-50'} transition-colors cursor-pointer group`}
                    onClick={() => !busy && selectProspect && selectProspect(prospect)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-primary bg-surface border-tertiary rounded focus:ring-primary-500 cursor-pointer"
                        aria-label={`Sélectionner ${prospect.organizationName || prospect.lastName || prospect.firstName || "ce prospect"}`}
                        disabled={busy}
                        checked={isSelected}
                        onChange={(e) => toggleRow(prospect._id || prospect.id, e)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-primary font-medium">{prospect.type}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">
                      {prospect.type === "Personne Morale" ? prospect.organizationName : prospect.lastName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">{prospect.firstName || "-"}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-subtle">{prospect.email || "-"}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-subtle">{prospect.phone || "-"}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusInfo(prospect.status).color}`}>
                        {getStatusInfo(prospect.status).icon}
                        {prospect.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                      <div className="flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Tooltip content="Voir la fiche">
                          <button
                            type="button"
                            disabled={busy}
                            onClick={(e) => { e.stopPropagation(); onViewDetails && onViewDetails(prospect._id || prospect.id); }}
                            className="group/btn relative p-2 text-primary-600 rounded-full transition-colors overflow-hidden"
                          >
                            <span className="absolute inset-0 bg-primary-50 rounded-full scale-0 group-hover/btn:scale-100 transition-transform duration-300 ease-out z-0"></span>
                            <Eye className="w-4 h-4 relative z-10 group-hover/btn:scale-110 transition-transform duration-300" />
                          </button>
                        </Tooltip>
                        <Tooltip content="Modifier">
                          <button
                            type="button"
                            disabled={busy}
                            onClick={(e) => { e.stopPropagation(); onEdit && onEdit(prospect); }}
                            className="group/btn relative p-2 text-warning-600 rounded-full transition-colors overflow-hidden"
                          >
                            <span className="absolute inset-0 bg-warning-50 rounded-full scale-0 group-hover/btn:scale-100 transition-transform duration-300 ease-out z-0"></span>
                            <Pencil className="w-4 h-4 relative z-10 group-hover/btn:scale-110 transition-transform duration-300" />
                          </button>
                        </Tooltip>
                        <Tooltip content="Archiver">
                          <button
                            type="button"
                            disabled={busy}
                            onClick={(e) => handleArchive(prospect._id || prospect.id, e)}
                            className="group/btn relative p-2 text-danger-600 rounded-full transition-colors overflow-hidden"
                          >
                            <span className="absolute inset-0 bg-danger-50 rounded-full scale-0 group-hover/btn:scale-100 transition-transform duration-300 ease-out z-0"></span>
                            <Trash2 className="w-4 h-4 relative z-10 group-hover/btn:scale-110 transition-transform duration-300" />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {!loading && totalProspects > 0 && (
        <PaginationFooter
          page={currentPage + 1}
          pageSize={pageSize}
          totalItems={totalProspects}
          onPageChange={nextPage => onPageChange(nextPage - 1)}
          disabled={busy}
          ariaLabel="Pagination des prospects"
          pageSizeOptions={[5, 10, 25, 50, 100]}
          onPageSizeChange={onPageSizeChange}
          summaryText={summaryText}
        />
      )}
    </div>
  );
}
